import React, { useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import cn from 'classnames';
import axios from 'axios';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import FavoritePoseItem from '../../../favorite-pose-item';
import PreviewImage from '../../../PreviewImage';
import Typography from '../../../Typography';
import Button from '../../../Button';
import Spinner from '../../../Spinner';
import Modal from '../../../modal';
import CloseIcon from '../../../svg-icons/close-icon';
import {
  getAppropriateBg,
  getAppropriateForeground,
} from '../../_ElementaryMultiPose/MultiPosePackageBuilder/steps/FavoritePoseSelection';
import { CustomerContext } from '../../../../contexts/CustomerContext';
import usePackagePoses from '../../../../hooks/usePackagePoses';
import usePricelist from '../../../../hooks/usePricelist';
import { OrderPackage, Option } from '../../../../models';
import QueryKeys from '../../../../utils/QueryKeys';
import routes from '../../../../routes';
import styles from './YearbookPoseSelection.module.sass';
import modalClasses from '../../favorite-pose-selection/favorite-pose-selection.module.sass';

const DRESS_CODE_NOTE =
  'Your yearbook photo must be formal attire and meet your school’s Senior YB '
  + 'dress code. Cap & gown photos are not accepted.';

// Identify the package's $0 "yearbook" Option the same way the rest of the senior
// flow does (senior-portrait-option-item): the Option whose name includes "yearbook".
const isYearbookOption = (option) => option?.name?.toLowerCase().includes('yearbook');

/**
 * Senior-only entry step: pick the official yearbook pose of record.
 *
 * - Single-select from ALL of the student's photos (no favorites pre-step).
 * - Pre-selects an existing is_yearbook_pose (set at the sitting) with
 *   "Keep & continue" / "Change"; otherwise nothing is pre-selected and the
 *   student must actively pick a pose before continuing.
 * - On confirm: (a) POST /api/senior_images/select_yearbook to flag the pose,
 *   (b) upsert a $0 yearbook OrderPackage into the cart (no sheets),
 *   (c) advance to the package step.
 *
 * Does NOT modify the shared FavoritePoseSelection — it only reuses its exported
 * pose/preview helpers and the FavoritePoseItem/PreviewImage building blocks.
 */
export default function YearbookPoseSelection({
  backgrounds,
  background,
  poses = [],
  packageId,
}) {
  const history = useHistory();
  const { customer } = useContext(CustomerContext);
  const queryClient = useQueryClient();
  const { data: pricelist } = usePricelist(packageId);
  const { data: packagePoses } = usePackagePoses(packageId);

  // The pose the school already has on record (set at the sitting), if any.
  const existingYearbookPose = useMemo(
    () => poses.find((pose) => pose.isYearbookPose === true),
    [poses],
  );

  // The senior leads with this step, so they pick the yearbook pose from ALL of
  // their photos (there is no favorites pre-selection). An existing is_yearbook_pose
  // (set at the sitting) is already in this list and gets pre-selected below.
  const selectablePoses = useMemo(() => poses.filter(Boolean), [poses]);

  // Pre-selects an existing sitting-time yearbook pose; the student can change it
  // by clicking a different photo, then Continue. No separate "Change" affordance.
  const [selectedId, setSelectedId] = useState(existingYearbookPose?.id || null);
  const [modalImage, setModalImage] = useState();
  const [modalPose, setModalPose] = useState();
  const [saving, setSaving] = useState(false);

  const isFetching = useIsFetching();

  if (isFetching) return null;

  const onPoseClick = (id) => setSelectedId(id);

  const handleImageClick = (pose, foregroundUrl) => {
    setModalImage(foregroundUrl);
    setModalPose(pose);
  };

  const advanceToPackages = () => {
    history.push(routes.seniors.packageBuilder(packageId, {
      step: 'select-package',
    }));
  };

  // Upsert the $0 yearbook OrderPackage into the cart. Mirrors how SeniorMultiPose
  // builds an OrderPackage (cartId/studentId/optionId/packageId/seniorImageId) but
  // with no paid sheets. Idempotent: skip if a yearbook OrderPackage already exists
  // for this cart (so re-entering the step doesn't create a duplicate $0 line).
  const upsertYearbookOrderPackage = async (poseId) => {
    const cartId = customer?.cart?.id;
    if (!cartId) return;

    const options = (await Option
      .where({ packageId })
      .includes(['image_types'])
      .all()).data;
    const yearbookOption = options.find(isYearbookOption);
    if (!yearbookOption) return;

    // Already in this cart? Don't duplicate. Query the server so we don't depend on
    // the (possibly unloaded) cart.orderPackages relationship in context.
    // NOTE: the order_packages#index endpoint 404s for non-admin (customer) sessions
    // unless a student_id filter is present (anti-enumeration guard), so scope by the
    // current student — which is correct anyway, the cart is for this student.
    const existing = (await OrderPackage
      .where({ cartId, optionId: yearbookOption.id, studentId: customer.currentStudent.id })
      .per(1)
      .all()).data;
    if (existing.length > 0) return;

    const orderPackage = new OrderPackage();
    orderPackage.cartId = cartId;
    orderPackage.studentId = customer.currentStudent.id;
    orderPackage.optionId = yearbookOption.id;
    orderPackage.packageId = packageId;
    orderPackage.apiOption = yearbookOption;
    orderPackage.seniorImageId = poseId;

    await orderPackage.save();
  };

  const confirmSelection = async (poseId) => {
    if (!poseId) {
      alert('Please select your yearbook photo to continue.');
      return;
    }

    setSaving(true);
    try {
      // 1. Flag the pose as the official yearbook pose of record (clears any prior).
      //    Endpoint is NOT under the /api/v1 baseURL, so override baseURL to root.
      //    Send the CSRF token: Api::ApiController inherits Rails forgery protection,
      //    and a tokenless cross-fetch POST would otherwise be rejected or run with a
      //    null session (losing the access-code'd customer the authz depends on).
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      await axios.post(
        '/api/senior_images/select_yearbook',
        { pose_id: poseId },
        { baseURL: '/', headers: { 'X-CSRF-Token': csrfToken } },
      );

      // 2. Put the required $0 yearbook line in the cart.
      await upsertYearbookOrderPackage(poseId);

      queryClient.invalidateQueries({ queryKey: [QueryKeys.cart, QueryKeys.numItems] });
      // Refetch poses so the Builder's required-yearbook gate sees the new
      // is_yearbook_pose and lets the senior into the package step.
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.STUDENT_IMAGES] });

      // 3. Move on to the (optional) package upsell.
      advanceToPackages();
    } catch (error) {
      console.error('Failed to set yearbook pose:', error);
      alert('Something went wrong setting your yearbook photo. Please try again.');
      setSaving(false);
    }
  };

  const renderModal = () => {
    const bg = getAppropriateBg(packagePoses, modalPose, backgrounds, pricelist) || backgrounds[0];
    const foreground = getAppropriateForeground(packagePoses, modalPose, pricelist);

    return (
      <Modal open>
        <div className={modalClasses.modalContent}>
          <div className={modalClasses.iconWrapper}>
            <CloseIcon className={modalClasses.closeIcon} onClick={() => setModalImage(undefined)} />
          </div>
          <PreviewImage
            foregroundUrl={modalImage}
            backgroundUrl={bg?.imageUrl || backgrounds[0]?.imageUrl}
            actualForegroundUrl={foreground?.imageUrl}
          />
        </div>
      </Modal>
    );
  };

  return (
    <div style={{ maxWidth: '992px', margin: '0 auto' }}>
      <div className={styles.imageContainer}>
        <div className={styles.instructions}>
          <Typography align="center" weight="bold">
            Choose your photo for the yearbook
          </Typography>
        </div>
        <div className={styles.dressCode}>{DRESS_CODE_NOTE}</div>

        {existingYearbookPose && (
          <div className={styles.existingNote}>
            Your yearbook photo ✓
          </div>
        )}

        {modalImage && renderModal()}

        <div
          className={cn(
            styles.container,
            modalImage && styles.hidden,
            selectablePoses.length === 1 ? styles.onePoseContainer : '',
          )}
        >
          {selectablePoses.map((pose, i) => {
            const priorityBg = getAppropriateBg(packagePoses, pose, backgrounds, pricelist);
            const foreground = getAppropriateForeground(packagePoses, pose, pricelist);
            const isSelected = pose.id === selectedId;

            return (
              <div key={pose.id} className={cn(isSelected ? styles.selected : '')}>
                <FavoritePoseItem
                  index={i}
                  onImageClick={handleImageClick}
                  background={backgrounds ? backgrounds[i % backgrounds.length].imageUrl : background}
                  priorityBg={priorityBg}
                  backgrounds={backgrounds || []}
                  onFavoriteClick={onPoseClick}
                  id={pose.id}
                  foreground2={pose}
                  foreground={pose.watermarkUrl}
                  actualForeground={foreground?.imageUrl}
                  fileName={pose.imageFileName}
                  favorited={isSelected}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.btnContainer}>
        {saving && <Spinner color="blue" />}

        {!saving && (
          <Button
            bold
            circular
            fullWidth
            variant="primary"
            classes={!selectedId ? styles.lowOpacity : ''}
            onClick={() => confirmSelection(selectedId)}
          >
            Continue
          </Button>
        )}
      </div>
    </div>
  );
}
